import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DownloadIcon, FileUpIcon, HistoryIcon } from 'lucide-react'
import { useId, useState, type SyntheticEvent } from 'react'
import { toast } from 'sonner'

import { CORPORATE_KEY, corporateApi } from '@/api/corporate'
import { ApiError } from '@/api/errors'
import type { CorporateDocument, CorporateDocumentCategory } from '@/api/types'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useResetOnOpen } from '@/hooks/use-reset-on-open'
import { DOCUMENT_CATEGORY_LABELS, documentChains } from '@/lib/corporate'
import { formatBytes, formatDateOnly, formatDateTime } from '@/lib/format'

export const Route = createFileRoute('/_app/corporate/pieces')({
  component: DocumentsPage,
})

const CATEGORIES = Object.keys(DOCUMENT_CATEGORY_LABELS) as CorporateDocumentCategory[]

async function download(document: CorporateDocument) {
  const { blob } = await corporateApi.documentContent(document.id)
  const url = URL.createObjectURL(blob)
  const link = window.document.createElement('a')
  link.href = url
  link.download = document.file.name
  link.click()
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 60_000)
}

/**
 * Les pièces du livre (lot 45) : statuts, règlements, certificats, avis
 * déposés, rapports annuels, conventions. Une pièce ne se supprime jamais : une
 * nouvelle version la remplace, et l'ancienne reste lisible.
 */
function DocumentsPage() {
  const [uploading, setUploading] = useState<{ replaces: CorporateDocument | null } | null>(null)
  const documents = useQuery({
    queryKey: [...CORPORATE_KEY, 'documents'],
    queryFn: corporateApi.documents,
  })
  const chains = documentChains(documents.data ?? [])
  return (
    <Frame className="space-y-4" data-testid="pieces">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Pièces</h2>
        <Button
          type="button"
          onClick={() => {
            setUploading({ replaces: null })
          }}
        >
          <FileUpIcon />
          Téléverser une pièce
        </Button>
      </div>
      {documents.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : documents.isError ? (
        <FormError>Les pièces n’ont pas pu être lues.</FormError>
      ) : chains.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Aucune pièce au livre : commencez par les statuts et le certificat de constitution.
        </p>
      ) : (
        CATEGORIES.filter((category) => chains.some(([head]) => head?.category === category)).map(
          (category) => (
            <section
              key={category}
              className="space-y-1"
              aria-label={DOCUMENT_CATEGORY_LABELS[category]}
            >
              <h3 className="text-sm font-medium text-muted-foreground">
                {DOCUMENT_CATEGORY_LABELS[category]}
              </h3>
              <ul className="divide-y rounded-lg border">
                {chains
                  .filter(([head]) => head?.category === category)
                  .map((chain) => (
                    <DocumentRow
                      key={chain[0]?.id}
                      chain={chain}
                      onReplace={(document) => {
                        setUploading({ replaces: document })
                      }}
                    />
                  ))}
              </ul>
            </section>
          ),
        )
      )}
      <UploadDialog
        open={uploading !== null}
        replaces={uploading?.replaces ?? null}
        onOpenChange={(open) => {
          if (!open) setUploading(null)
        }}
      />
    </Frame>
  )
}

function DocumentRow({
  chain,
  onReplace,
}: {
  chain: CorporateDocument[]
  onReplace: (document: CorporateDocument) => void
}) {
  const [showVersions, setShowVersions] = useState(false)
  const head = chain[0]
  const get = useMutation({ mutationFn: download })
  if (!head) return null
  const older = chain.slice(1)
  return (
    <li className="space-y-1 p-3 text-sm" data-testid="piece">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 font-medium">{head.title}</span>
        <span className="text-muted-foreground">
          {head.effective_on ? `En vigueur le ${formatDateOnly(head.effective_on)}` : ''}
          {head.filed_on ? ` · déposée le ${formatDateOnly(head.filed_on)}` : ''}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={get.isPending}
          onClick={() => {
            get.mutate(head)
          }}
        >
          <DownloadIcon />
          {head.file.name} · {formatBytes(head.file.size)}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            onReplace(head)
          }}
        >
          Nouvelle version
        </Button>
      </div>
      {older.length > 0 && (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-primary"
          onClick={() => {
            setShowVersions((v) => !v)
          }}
        >
          <HistoryIcon className="size-3.5" aria-hidden="true" />
          {showVersions ? 'Masquer' : 'Voir'}{' '}
          {older.length === 1
            ? 'la version précédente'
            : `les ${String(older.length)} versions précédentes`}
        </button>
      )}
      {showVersions && (
        <ul className="space-y-1 pl-4 text-xs text-muted-foreground" data-testid="versions">
          {older.map((version) => (
            <li key={version.id} className="flex flex-wrap items-center gap-2">
              <span>
                {version.title} — téléversée le {formatDateTime(version.uploaded_at)}
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  get.mutate(version)
                }}
              >
                <DownloadIcon />
                {version.file.name}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {get.error && <FormError>La pièce n’a pas pu être téléchargée.</FormError>}
    </li>
  )
}

function UploadDialog({
  open,
  replaces,
  onOpenChange,
}: {
  open: boolean
  replaces: CorporateDocument | null
  onOpenChange: (open: boolean) => void
}) {
  const ids = {
    category: useId(),
    title: useId(),
    effective: useId(),
    filed: useId(),
    file: useId(),
  }
  const queryClient = useQueryClient()
  const [category, setCategory] = useState<CorporateDocumentCategory>('articles')
  const [title, setTitle] = useState('')
  const [effective, setEffective] = useState('')
  const [filed, setFiled] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  useResetOnOpen(open, replaces?.id ?? 'nouvelle-piece', () => {
    setCategory(replaces?.category ?? 'articles')
    setTitle(replaces?.title ?? '')
    setEffective('')
    setFiled('')
    setFile(null)
    setProgress(null)
    upload.reset()
  })
  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choisissez le fichier.')
      return corporateApi.uploadDocument(
        {
          file,
          category,
          title: title.trim(),
          effective_on: effective || null,
          filed_on: filed || null,
          replaces_id: replaces?.id ?? null,
        },
        { onProgress: setProgress },
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...CORPORATE_KEY, 'documents'] })
      onOpenChange(false)
      toast.success(
        replaces ? 'Nouvelle version au livre ; l’ancienne reste.' : 'Pièce ajoutée au livre.',
      )
    },
  })
  const submit = (event: SyntheticEvent) => {
    event.preventDefault()
    if (file && title.trim()) upload.mutate()
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {replaces ? `Nouvelle version de « ${replaces.title} »` : 'Téléverser une pièce'}
            </DialogTitle>
            <DialogDescription>
              Une pièce ne se supprime jamais : une nouvelle version la remplace, l’ancienne reste
              au livre.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={ids.category}>Catégorie</Label>
              <Select
                value={category}
                onValueChange={(value) => {
                  setCategory(value as CorporateDocumentCategory)
                }}
                disabled={replaces !== null}
              >
                <SelectTrigger id={ids.category} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((item) => (
                    <SelectItem key={item} value={item}>
                      {DOCUMENT_CATEGORY_LABELS[item]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={ids.title}>Titre</Label>
              <Input
                id={ids.title}
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value)
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={ids.effective}>En vigueur le (facultatif)</Label>
              <Input
                id={ids.effective}
                type="date"
                value={effective}
                onChange={(event) => {
                  setEffective(event.target.value)
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={ids.filed}>Déposée le (facultatif)</Label>
              <Input
                id={ids.filed}
                type="date"
                value={filed}
                onChange={(event) => {
                  setFiled(event.target.value)
                }}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor={ids.file}>Fichier</Label>
            <Input
              id={ids.file}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
              }}
            />
          </div>
          {progress !== null && upload.isPending && (
            <p className="text-sm text-muted-foreground">Envoi : {Math.round(progress * 100)} %</p>
          )}
          {upload.error && (
            <FormError>
              {upload.error instanceof ApiError
                ? upload.error.message
                : 'La pièce n’a pas pu être téléversée.'}
            </FormError>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={!file || !title.trim() || upload.isPending}
              data-testid="piece-televerser"
            >
              Téléverser
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
